import { Navigate, type RouteObject } from 'react-router';

import { MyAccountNotifications, myAccountPersonalDetails as MyAccountPersonalDetails, SearchSymbolsMob } from '../components';
import PayoutHistoryPage from '../components/Payouts/PayoutHistoryPage/PayoutHistoryPage';
import AddCryptoAddress from '../components/PayoutsInternalScreens/AddCryptoAddress/AddCryptoAddress';
import AddYourBankAccount from '../components/PayoutsInternalScreens/AddYourBankAccount/AddYourBankAccount';
import UploadBankStatement from '../components/PayoutsInternalScreens/AddYourBankAccount/UploadBankStatement/UploadBankStatement';
import PayoutsChoosePayment from '../components/PayoutsInternalScreens/PayoutsChoosePayment/PayoutsChoosePayment';
import WithdrawalAmount from '../components/PayoutsInternalScreens/WithdrawalAmount/WithdrawalAmount';
import WithdrawCryptoAmount from '../components/PayoutsInternalScreens/WithdrawCryptoAmount/WithdrawCryptoAmount';
import {
  BalancesheetStatement,
  BuyAndSell,
  CashFlowStatement,
  CryptoPayment,
  DemoAccountActivationError,
  Discover,
  EarningsCalendarInternalWrapper,
  EnrollPhoneVerified,
  EpicMoversInternalWrapper,
  Error,
  HotGappersInternalWrapper,
  IncomeStatement,
  LongTermInvestmentsInternalWrapper,
  MarketCapRadarInternalWrapper,
  Modify,
  MyProfile,
  Overview,
  PaymentMethodSelection,
  PaymentPlanError,
  Payouts,
  PlanCancel,
  PopularGroupTableWrapper,
  Product,
  Referrals,
  SymbolDetails,
  TradeHistory,
  TradingViewChart,
  VerificationPhoneNumber,
  VerificationPhoneOTP,
  Week52GainersAndLosersInternalWrapper,
} from '../containers';
import Dashboard from '../containers/Dashboard/Dashboard';
import PaymentPlanSuccess from '../containers/Errors/TradingPlan/PaymentPlanSuccess';
import PlanDetails from '../containers/PlanDetails/PlanDetails';
import { UI_ROUTE_MOCKED, UI_ROUTES } from '../util/routes';

import { type AuthenticatedRoutesFlags } from './routes.types';

/** Route config rendered once the user is authenticated. */
export const getAuthenticatedRoutes = ({ redirectTarget }: AuthenticatedRoutesFlags): RouteObject[] => [
  { path: `${UI_ROUTES.tradingView}/:name`, element: <TradingViewChart /> },
  { path: UI_ROUTES.error, element: <Error /> },
  { path: UI_ROUTES.dashboard, element: <Dashboard /> },
  { path: UI_ROUTES.dashboard + UI_ROUTE_MOCKED, element: <Dashboard /> },
  { path: UI_ROUTES.planDetails, element: <PlanDetails /> },
  { path: UI_ROUTES.myAccountNotifications, element: <MyAccountNotifications /> },
  { path: UI_ROUTES.myAccountPersonalDetails, element: <MyAccountPersonalDetails /> },
  { path: UI_ROUTES.overview, element: <Overview /> },
  { path: UI_ROUTES.discover, element: <Discover /> },
  { path: UI_ROUTES.discover + UI_ROUTE_MOCKED, element: <Discover /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.addYourBankAccount, element: <AddYourBankAccount /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.addCryptoAddress, element: <AddCryptoAddress /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.withdrawalAmount, element: <WithdrawalAmount /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.withdrawCryptoAmount, element: <WithdrawCryptoAmount /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.uploadBankStatement, element: <UploadBankStatement /> },
  { path: UI_ROUTES.payouts, element: <Payouts /> },
  { path: UI_ROUTES.payouts + UI_ROUTES.payoutsMethods, element: <PayoutsChoosePayment /> },
  { path: UI_ROUTES.referrals, element: <Referrals /> },
  { path: UI_ROUTES.products, element: <Product /> },
  { path: UI_ROUTES.paymentError, element: <PaymentPlanError /> },
  { path: UI_ROUTES.demoAccountActivationError, element: <DemoAccountActivationError /> },
  { path: `${UI_ROUTES.symbol}/:name${UI_ROUTES.cashFlowStatement}`, element: <CashFlowStatement /> },
  { path: `${UI_ROUTES.symbol}/:name${UI_ROUTES.incomeStatement}`, element: <IncomeStatement /> },
  { path: `${UI_ROUTES.symbol}/:name${UI_ROUTES.balancesheetStatement}`, element: <BalancesheetStatement /> },
  { path: `${UI_ROUTES.symbol}/:name`, element: <SymbolDetails /> },
  { path: `${UI_ROUTES.buy}/:name`, element: <BuyAndSell /> },
  { path: `${UI_ROUTES.sell}/:name`, element: <BuyAndSell /> },
  { path: UI_ROUTES.searchSymbolsMob, element: <SearchSymbolsMob /> },
  { path: UI_ROUTES.enrollPhoneVerification, element: <VerificationPhoneNumber /> },
  { path: UI_ROUTES.enrollPhoneVerified, element: <EnrollPhoneVerified /> },
  { path: UI_ROUTES.paymentMethodSelection, element: <PaymentMethodSelection /> },
  { path: UI_ROUTES.cryptoPayment, element: <CryptoPayment /> },
  { path: UI_ROUTES.enrollPhoneVerificationOtp, element: <VerificationPhoneOTP /> },
  { path: UI_ROUTES.paymentCancel, element: <PlanCancel /> },
  { path: UI_ROUTES.modify, element: <Modify /> },
  { path: `${UI_ROUTES.discover + UI_ROUTES.popularGroup}/:sector`, element: <PopularGroupTableWrapper /> },
  { path: UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow, element: <Week52GainersAndLosersInternalWrapper /> },
  { path: UI_ROUTES.discover + UI_ROUTES.gapTrackers, element: <HotGappersInternalWrapper /> },
  // TODO: Enable New on the Market feature when the backend team fiexes the konown issues
  { path: UI_ROUTES.discover + UI_ROUTES.earningsCalendar, element: <EarningsCalendarInternalWrapper /> },
  { path: UI_ROUTES.discover + UI_ROUTES.epicMoversToday, element: <EpicMoversInternalWrapper /> },
  { path: UI_ROUTES.discover + UI_ROUTES.marketCapRadar, element: <MarketCapRadarInternalWrapper /> },
  { path: UI_ROUTES.tradeHistory, element: <TradeHistory /> },
  { path: UI_ROUTES.payoutHistory, element: <PayoutHistoryPage /> },
  { path: UI_ROUTES.myProfile, element: <MyProfile /> },
  // it is currnetcly not used in HammerWeb
  {
    path: UI_ROUTES.discover + UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath,
    element: <LongTermInvestmentsInternalWrapper />,
  },
  { path: UI_ROUTES.paymentSuccess, element: <PaymentPlanSuccess /> },
  { path: '*', element: <Navigate to={redirectTarget.to} state={redirectTarget.state} replace /> },
];
